import { useCallback } from 'react';
import { useTables } from '@/hooks/TablesContext';
import pacote from '../../../package.json';
import { toCoded } from '@/shared/coded';
import { yieldToMain } from '@/shared/yieldToMain';
import { openDB as idb, deleteDB as idbDeleteDatabase, unwrap, IDBPDatabase } from 'idb';
import { deleteBfSellerDb } from '@/shared/bfSeller/bfSellerDb';
import { BF_SELLER_STORES, type BfSellerStore } from '@/shared/bfSeller/bfSellerSchema';
import { hasDatasetRows, writeLocalDatasetTable } from '@/shared/bfSeller/bfSellerWriter';
import { readRestricoesDoCliente } from '@/shared/bfSeller/bfSellerProducts';
import {
  hydrateCategoria,
  hydrateCicloVenda,
  hydrateCliente,
  hydrateClienteBoletos,
  hydrateClienteCortes,
  hydrateDicionarioCategorias,
  hydrateDicionarioProdutos,
  hydrateEventos,
  hydratePagebuilder,
  hydrateParametroPrecoBase,
  hydratePreco,
  hydratePrecoPromocaoCliente,
  hydrateRecomendacaoBigData,
  hydrateRestricaoProdutoCliente,
  hydrateRows,
  hydrateSaldoFlex,
  hydrateSingleton,
  hydrateUltimaCompra,
} from '@/shared/bfSeller/bfSellerHydrate';
import { ProductProps, TableTypes, tableKeys } from '@/types';

export type Tables = (typeof tableKeys)[number];
const listTables: Tables[] = [...tableKeys];
const TABLE_ALIASES: Record<string, Tables> = {
  preco_novo: 'preco',
  saldo_flex_novo: 'saldo_flex',
  campanhaClientes: 'campanha_cliente',
};

/** Mesma regra que `setOnDB`: não aceita array/objeto vazio (exceto quando `force` no write único). */
export const isValidDbPayload = (data: any): boolean =>
  !!(
    data &&
    typeof data === 'object' &&
    !(Array.isArray(data) && data.length === 0) &&
    !(typeof data === 'object' && !Array.isArray(data) && Object.keys(data).length === 0)
  );

export const resolveTableNameForWrite = (name: string): Tables | null => {
  const normalized = (TABLE_ALIASES[name] || name) as Tables;
  return listTables.includes(normalized) ? normalized : null;
};

export { toCoded };

const DB_NAME = toCoded('BF_SELLER_EVENTS');
const DB_IMAGES_NAME = toCoded('BF_SELLER_IMAGES');
const DB_VERSION = Number(pacote.version.replace('.', ''));
const IMAGES_DB_PRODUCT_KEY_MIN_VERSION = 15;
const IMAGES_DB_OPEN_VERSION = Math.max(DB_VERSION, IMAGES_DB_PRODUCT_KEY_MIN_VERSION);

/**
 * Store do `BF_SELLER_IMAGES` onde o zip baixado fica inteiro, como veio do S3.
 *
 * O sync não descompacta mais: descompactar o zip, converter cada entrada em `Blob` e gravar
 * imagem por imagem era a fase mais cara do run, e nada disso precisa acontecer enquanto o
 * arquivo está sendo baixado. Aqui entra um registro por arquivo — a URL é a chave, então
 * rebaixar o mesmo zip sobrescreve em vez de duplicar.
 */
const IMAGES_ZIP_STORE = toCoded('images_zip');

/** Um zip de imagens gravado. `zip` é o corpo da resposta, sem nenhum tratamento. */
export type ImagesZipRow = {
  url: string;
  zip: Blob;
  tipo: string;
  loja: number | null;
  segmento: number | null;
  bytes: number;
  dtUpdate?: string;
  savedAt: string;
};

/** Uma imagem pronta para gravar: bytes crus, nunca `Blob`. Ver `abrirSessaoImagens`. */
export type ImagemParaGravar = { bytes: Uint8Array; tipo?: string };

/**
 * Linha da store `imagens` → o que o `<img>` consegue exibir.
 *
 * Três formatos convivem: bytes (o atual), `Blob` (bancos gravados antes desta mudança) e base64
 * (o dataset de imagens em JSON, que não vem zipado). O `Blob` é remontado na leitura, uma imagem
 * por vez — é barato, e é o que permite não gravar `Blob` nenhum.
 */
const materializarImagem = (row: any): Blob | string | null => {
  const bruto = row?.imagem;
  if (bruto == null) return null;
  if (bruto instanceof Blob) return bruto;
  if (typeof bruto === 'string') return bruto.replace(/^data:.*;base64,/i, '');

  const tipo = typeof row?.tipo === 'string' && row.tipo ? row.tipo : 'image/webp';
  if (bruto instanceof ArrayBuffer) return new Blob([bruto], { type: tipo });
  if (ArrayBuffer.isView(bruto)) return new Blob([bruto as unknown as BlobPart], { type: tipo });

  return null;
};

/** Promessas em voo por tabela — evita disputa/retry quando vários hooks pedem a mesma tabela. */
const inFlightTableLoads: Partial<Record<string, Promise<unknown>>> = {};

/** Lote de gravação de imagens: mantém o pico de memória baixo e dá respiro à UI entre eles. */
const IMAGES_CHUNK_SIZE = 200;

/** Tempo extra após `deleteDatabase` resolver (mobile/PWA costuma precisar de um tick a mais). */
const IDB_DELETE_SETTLE_MS = 450;

const settleAfterIdbDelete = () => new Promise<void>((r) => setTimeout(r, IDB_DELETE_SETTLE_MS));

export const deleteDB = async (): Promise<void> => {
  try {
    await idbDeleteDatabase(DB_NAME, {
      blocked() {
        console.warn('[IDB] Exclusão do banco principal bloqueada até outras conexões fecharem');
      },
    });
    await settleAfterIdbDelete();
  } catch (error) {
    console.error('Erro ao deletar banco de dados:', error);
  }
};

export const deleteImagesDB = async (): Promise<void> => {
  try {
    await idbDeleteDatabase(DB_IMAGES_NAME, {
      blocked() {
        console.warn('[IDB] Exclusão do banco de imagens bloqueada até outras conexões fecharem');
      },
    });
    await settleAfterIdbDelete();
  } catch (error) {
    console.error('Erro ao deletar banco de imagens:', error);
  }
};

/**
 * Apaga os IndexedDB do app em sequência e só retorna quando o `idb` concluir (Promise real).
 * Usado no reset forçado por versão; retorna `false` se alguma exclusão falhar.
 */
export const deleteAppIndexedDatabasesCompletely = async (): Promise<boolean> => {
  let ok = true;
  try {
    await deleteBfSellerDb();
  } catch (e) {
    ok = false;
    console.error('[IDB] Falha ao excluir o BF_SELLER_DATASETS:', e);
  }
  try {
    await idbDeleteDatabase(DB_NAME, {
      blocked() {
        console.warn('[IDB] reset: banco principal bloqueado, aguardando fechamento de conexões');
      },
    });
    await settleAfterIdbDelete();
  } catch (e) {
    ok = false;
    console.error('[IDB] Falha ao excluir banco principal:', e);
  }
  try {
    await idbDeleteDatabase(DB_IMAGES_NAME, {
      blocked() {
        console.warn('[IDB] reset: banco de imagens bloqueado, aguardando fechamento de conexões');
      },
    });
    await settleAfterIdbDelete();
  } catch (e) {
    ok = false;
    console.error('[IDB] Falha ao excluir banco de imagens:', e);
  }
  return ok;
};

const BATE_FORTE_TABLES: Tables[] = ['offline_events'];

const BATE_FORTE_STORES = new Set(BATE_FORTE_TABLES.map((name) => toCoded(name)));

const createObjectStore = (db: IDBPDatabase, name: string): void => {
  const tableName = toCoded(name);
  if (!db.objectStoreNames.contains(tableName)) {
    db.createObjectStore(tableName, {
      keyPath: toCoded(name),
      autoIncrement: true,
    });
  }
};

/** Cria o que falta e apaga o que ficou para trás — as 26 stores que a migração deixou órfãs. */
const syncObjectStores = (db: IDBPDatabase): void => {
  for (const store of Array.from(db.objectStoreNames)) {
    if (!BATE_FORTE_STORES.has(store)) db.deleteObjectStore(store);
  }
  BATE_FORTE_TABLES.forEach((name) => createObjectStore(db, name));
};

/**
 * Esvazia as stores órfãs que ainda existirem.
 *
 * quase nunca muda, então o upgrade que apaga essas stores pode levar muitos releases para rodar.
 * Até lá o catálogo e o preço antigos seguem ocupando disco — num aparelho apertado de espaço isso
 * é o que faz o navegador descartar o banco novo. Esvaziar o conteúdo não depende de upgrade:
 * a store continua no schema, sem os megabytes.
 */
const OBSOLETE_STORES_CLEARED_KEY = 'bate_forte_stores_orfas_limpas';

const clearObsoleteStores = async (db: IDBPDatabase): Promise<void> => {
  try {
    if (localStorage.getItem(OBSOLETE_STORES_CLEARED_KEY) === '1') return;

    const orfas = Array.from(db.objectStoreNames).filter((store) => !BATE_FORTE_STORES.has(store));
    if (orfas.length) {
      const tx = db.transaction(orfas, 'readwrite');
      for (const store of orfas) void tx.objectStore(store).clear();
      await tx.done;
      console.info(`[IDB] ${orfas.length} stores órfãs esvaziadas`);
    }

    localStorage.setItem(OBSOLETE_STORES_CLEARED_KEY, '1');
  } catch (error) {
    console.info('[IDB] não foi possível esvaziar as stores órfãs:', error);
  }
};

const checkAndCreateMissingTables = async (): Promise<IDBPDatabase> => {
  const db = await idb(DB_NAME, DB_VERSION);
  const faltando = BATE_FORTE_TABLES.some((name) => !db.objectStoreNames.contains(toCoded(name)));
  if (!faltando) return db;

  // Versão corrente + 1: subir sempre acima do que está no disco, nunca abaixo — abrir com versão
  // menor lança `VersionError`, e o `catch` do `openDBConnection` apaga o banco.
  const proximaVersao = db.version + 1;
  db.close();

  return idb(DB_NAME, proximaVersao, {
    upgrade(db) {
      syncObjectStores(db);
    },
  });
};

export const openDBConnection = async (): Promise<IDBPDatabase> => {
  try {
    let db = await idb(DB_NAME, DB_VERSION, {
      upgrade(db) {
        syncObjectStores(db);
      },
    });

    const faltando = BATE_FORTE_TABLES.some((name) => !db.objectStoreNames.contains(toCoded(name)));
    if (faltando) {
      db.close();
      db = await checkAndCreateMissingTables();
    }

    await clearObsoleteStores(db);

    return db;
  } catch (error) {
    console.error('Erro ao abrir conexão com banco de dados:', error);
    await deleteDB();
    // await unregisterServiceWorker({});
    throw error;
  }
};

const createImagesZipStore = (db: IDBPDatabase): void => {
  if (!db.objectStoreNames.contains(IMAGES_ZIP_STORE)) {
    db.createObjectStore(IMAGES_ZIP_STORE, { keyPath: 'url' });
  }
};

/**
 * Abre o banco de imagens, criando o que faltar.
 *
 * A abertura começa **sem versão**, de propósito: `IMAGES_DB_OPEN_VERSION` é derivada do
 * `package.json` e quase não se move, enquanto a versão no disco sobe sozinha toda vez que uma
 * store nova precisa nascer fora de um release. Pedir uma versão menor que a gravada lança
 * `VersionError` — abrir sem versão diz o que está lá e só então se decide se há o que migrar.
 */
export const openImagesDBConnection = async (): Promise<IDBPDatabase> => {
  try {
    const storeName = toCoded('imagens');
    const db = await idb(DB_IMAGES_NAME);

    // Banco anterior à chave por `COD_PRODUTO`: a store é refeita, o conteúdo antigo não serve.
    const precisaRefazerImagens = db.version < IMAGES_DB_PRODUCT_KEY_MIN_VERSION;
    const faltaAlgumaStore = !db.objectStoreNames.contains(storeName) || !db.objectStoreNames.contains(IMAGES_ZIP_STORE);

    if (!precisaRefazerImagens && !faltaAlgumaStore) return db;

    // Sempre acima do que está no disco, e nunca abaixo do piso da migração da chave.
    const proximaVersao = Math.max(db.version + 1, IMAGES_DB_OPEN_VERSION);
    db.close();

    return await idb(DB_IMAGES_NAME, proximaVersao, {
      upgrade(db, oldVersion) {
        if (oldVersion < IMAGES_DB_PRODUCT_KEY_MIN_VERSION && db.objectStoreNames.contains(storeName)) {
          db.deleteObjectStore(storeName);
        }
        if (!db.objectStoreNames.contains(storeName)) {
          db.createObjectStore(storeName, { keyPath: 'COD_PRODUTO' });
        }
        createImagesZipStore(db);
      },
      blocked() {
        console.warn('[IDB] upgrade do banco de imagens aguardando outras conexões fecharem');
      },
      blocking() {
        // Outra aba pediu um upgrade. Não se fecha esta conexão aqui: ela pode estar no meio de
        // uma gravação, e toda função deste arquivo fecha a sua no `finally` — o bloqueio se
        // resolve sozinho em milissegundos.
        console.info('[IDB] banco de imagens bloqueando o upgrade de outra aba');
      },
    });
  } catch (error) {
    console.error('Erro ao abrir conexão com banco de imagens:', error);
    throw error;
  }
};

const isLocalTable = (name: Tables): boolean => BATE_FORTE_TABLES.includes(name);

/**
 * Reconstrói o formato legado de cada tabela a partir das linhas chave-valor do `BF_SELLER_DATASETS`.
 * Os joins que saíram da gravação (preço no produto, ciclo no cliente) acontecem aqui.
 */
const hydrateFromBfSeller = async (
  name: Tables,
  client?: number,
  dicionarioCache?: Record<number, ProductProps> | null
): Promise<any> => {
  switch (name) {
    case 'dicionario_produtos':
      return hydrateDicionarioProdutos();
    case 'produtos': {
      // `dicionarioCache` vem do TablesContext quando o dicionário já foi montado neste boot —
      // o `LoaderContext` pede as duas tabelas em sequência, e sem isso o dicionário inteiro
      // seria remontado do zero na segunda.
      const [dict, restritos] = await Promise.all([
        dicionarioCache ?? hydrateDicionarioProdutos(),
        client != null ? readRestricoesDoCliente(client) : Promise.resolve(new Set<number>()),
      ]);
      return Object.values(dict).filter((product) => !restritos.has(Number(product.COD_PRODUTO)));
    }
    case 'preco':
      return hydratePreco();
    case 'cliente':
      return hydrateCliente();
    case 'categoria':
      return hydrateCategoria();
    case 'dicionario_categorias':
      return hydrateDicionarioCategorias();
    case 'eventos':
      return hydrateEventos();
    case 'cliente_boletos':
      return hydrateClienteBoletos();
    case 'cliente_cortes':
      return hydrateClienteCortes();
    case 'precoPromocaoCliente':
      return hydratePrecoPromocaoCliente();
    case 'saldo_flex':
      return hydrateSaldoFlex();
    case 'restricaoProdutoCliente':
      return hydrateRestricaoProdutoCliente();
    case 'parametro_preco_base':
      return hydrateParametroPrecoBase();
    case 'pagebuilder':
      return hydratePagebuilder();
    case 'ciclo_venda_cliente_cliente':
      return hydrateCicloVenda();
    case 'recomendacaoBigData':
      return hydrateRecomendacaoBigData();
    case 'ultima_compra':
      return hydrateUltimaCompra();
    case 'campanha_cliente':
    case 'campanha_produto':
      return hydrateSingleton(name);
    default:
      return BF_SELLER_STORES.includes(name as BfSellerStore) ? hydrateRows(name as BfSellerStore) : [];
  }
};

const handleGet = async <T extends Tables>(name: T, currDB: IDBPDatabase): Promise<TableTypes[T]> => {
  try {
    const transaction = currDB.transaction(toCoded(name), 'readonly');
    const store = transaction.objectStore(toCoded(name));
    const allKeys = await store.getAllKeys();
    const lastKey = allKeys[allKeys.length - 1];
    if (lastKey == null) {
      return [] as TableTypes[T];
    }

    const lastData = await store.get(lastKey as IDBValidKey);
    const resp = (lastData?.data ?? []) as TableTypes[T];
    return name === 'restricaoProdutoCliente' ? resp || [] : resp;
  } catch (error) {
    console.info(`Erro ao obter dados da tabela ${name}:`, error);
    return [] as TableTypes[T];
  }
};

export const useDbFunctions = () => {
  const { tables, setOnTables, isLoading } = useTables();

  const normalizeTableName = (name: string): Tables | null => {
    const normalized = (TABLE_ALIASES[name] || name) as Tables;
    return listTables.includes(normalized) ? normalized : null;
  };

  const closeDBConnection = (db: IDBPDatabase | undefined): void => {
    if (db) {
      db.close();
    }
  };

  const getImageFromDB = async (COD_PRODUTO: number): Promise<Blob | string | null> => {
    try {
      const currDB = await openImagesDBConnection();
      const transaction = currDB.transaction(toCoded('imagens'), 'readonly');
      const store = transaction.objectStore(toCoded('imagens'));
      const row = await store.get(COD_PRODUTO);
      await transaction.done;
      closeDBConnection(currDB);
      return materializarImagem(row);
    } catch (error) {
      console.error('Erro ao obter imagem do banco:', error);
      return null;
    }
  };

  const setImagesOnDB = async (data: any, { reset = true }: { reset?: boolean } = {}): Promise<number> => {
    if (!isValidDbPayload(data)) return 0;

    const normalizeImagem = (raw: unknown): Blob | string | null => {
      if (raw instanceof Blob) return raw;
      if (typeof raw === 'string') return raw.replace(/^data:.*;base64,/i, '');
      return null;
    };

    const rows: Array<{ COD_PRODUTO: number; imagem: Blob | string }> = [];
    if (Array.isArray(data)) {
      for (const item of data) {
        const cod = Number(item?.COD_PRODUTO);
        const imagem = normalizeImagem(item?.imagem);
        if (!Number.isFinite(cod) || imagem == null) continue;
        rows.push({ COD_PRODUTO: cod, imagem });
      }
    } else if (data && typeof data === 'object') {
      for (const [k, v] of Object.entries(data)) {
        const cod = Number(k);
        const imagem = normalizeImagem(v);
        if (!Number.isFinite(cod) || imagem == null) continue;
        rows.push({ COD_PRODUTO: cod, imagem });
      }
    }

    if (!rows.length) return 0;

    let currDB: IDBPDatabase | undefined;
    try {
      currDB = await openImagesDBConnection().catch((err) => {
        throw new Error(`Erro ao abrir conexão com banco de dados para salvar imagens: ${err?.message ?? err}`);
      });

      const storeName = toCoded('imagens');

      if (reset) {
        const resetTx = currDB.transaction(storeName, 'readwrite');
        void resetTx
          .objectStore(storeName)
          .clear()
          .catch(() => {});
        await resetTx.done.catch((err) => {
          throw new Error(`Erro ao limpar imagens do banco de dados: ${err?.message ?? err}`);
        });
      }

      for (let start = 0; start < rows.length; start += IMAGES_CHUNK_SIZE) {
        const fim = Math.min(start + IMAGES_CHUNK_SIZE, rows.length);
        const tx = currDB.transaction(storeName, 'readwrite');
        // `put` cru: sem uma Promise e dois listeners por imagem. Quem espera é o `tx.done`.
        const store = (unwrap(tx) as unknown as IDBTransaction).objectStore(storeName);
        for (let i = start; i < fim; i += 1) {
          store.put(rows[i]);
        }
        await tx.done.catch((err) => {
          throw new Error(`Erro ao gravar lote de imagens: ${err?.message ?? err}`);
        });
        if (fim < rows.length) await yieldToMain();
      }

      return rows.length;
    } catch (error) {
      console.error('Erro ao salvar imagens no banco:', error);
      return Promise.reject(error);
    } finally {
      closeDBConnection(currDB);
    }
  };

  /**
   * Grava o zip recém-baixado, sem abrir.
   *
   * `reset` limpa a store — uma vez por sync, no primeiro zip do run, pelo mesmo motivo do purge
   * dos datasets: zip que sumiu da origem não pode ficar para sempre. Fazer por arquivo apagaria
   * o que o zip anterior gravou.
   */
  const setImagesZipOnDB = async (
    row: Omit<ImagesZipRow, 'savedAt'>,
    { reset = false }: { reset?: boolean } = {}
  ): Promise<boolean> => {
    if (!row?.url || !(row.zip instanceof Blob) || !row.zip.size) return false;

    let currDB: IDBPDatabase | undefined;
    try {
      currDB = await openImagesDBConnection().catch((err) => {
        throw new Error(`Erro ao abrir conexão com banco de dados para salvar o zip de imagens: ${err?.message ?? err}`);
      });

      if (reset) {
        const resetTx = currDB.transaction(IMAGES_ZIP_STORE, 'readwrite');
        void resetTx
          .objectStore(IMAGES_ZIP_STORE)
          .clear()
          .catch(() => {});
        await resetTx.done.catch((err) => {
          throw new Error(`Erro ao limpar os zips de imagens do banco de dados: ${err?.message ?? err}`);
        });
      }

      const tx = currDB.transaction(IMAGES_ZIP_STORE, 'readwrite');
      void tx
        .objectStore(IMAGES_ZIP_STORE)
        .put({ ...row, savedAt: new Date().toISOString() } satisfies ImagesZipRow)
        .catch(() => {});
      await tx.done.catch((err) => {
        throw new Error(`Erro ao gravar o zip de imagens: ${err?.message ?? err}`);
      });

      return true;
    } catch (error) {
      console.error('Erro ao salvar o zip de imagens no banco:', error);
      return Promise.reject(error);
    } finally {
      closeDBConnection(currDB);
    }
  };

  /**
   * Sessão de gravação de imagens: uma conexão para o trabalho inteiro.
   *
   * `setImagesOnDB` abre e fecha o banco a cada chamada — o que é certo para uma gravação avulsa e
   * caro para milhares de imagens em lotes, onde a abertura passa a ser paga dezenas de vezes.
   * Quem abre é responsável por `fechar()`.
   *
   * **A imagem entra como bytes, não como `Blob`.** Todo `Blob` gravado no IndexedDB vira um
   * arquivo próprio no disco: milhares de imagens viram milhares de criações de arquivo, e num
   * aparelho de flash lento é isso — não a descompactação — que domina o tempo do processo. Bytes
   * crus são gravados dentro da própria transação, junto com o resto do lote. O `Blob` é remontado
   * na leitura, uma imagem por vez (`materializarImagem`).
   */
  const abrirSessaoImagens = async (): Promise<{
    gravar: (lote: Record<number, ImagemParaGravar>) => Promise<number>;
    fechar: () => void;
  }> => {
    const currDB = await openImagesDBConnection();
    const storeName = toCoded('imagens');

    const gravar = async (lote: Record<number, ImagemParaGravar>): Promise<number> => {
      const rows: Array<{ COD_PRODUTO: number; imagem: Uint8Array; tipo?: string }> = [];
      for (const [chave, item] of Object.entries(lote)) {
        const cod = Number(chave);
        if (!Number.isFinite(cod) || !ArrayBuffer.isView(item?.bytes)) continue;
        rows.push({ COD_PRODUTO: cod, imagem: item.bytes, tipo: item.tipo });
      }
      if (!rows.length) return 0;

      for (let start = 0; start < rows.length; start += IMAGES_CHUNK_SIZE) {
        const fim = Math.min(start + IMAGES_CHUNK_SIZE, rows.length);
        const tx = currDB.transaction(storeName, 'readwrite');
        // `put` cru: sem uma Promise e dois listeners por imagem. Quem espera é o `tx.done`.
        const store = (unwrap(tx) as unknown as IDBTransaction).objectStore(storeName);
        for (let i = start; i < fim; i += 1) store.put(rows[i]);
        await tx.done;
      }

      return rows.length;
    };

    return { gravar, fechar: () => closeDBConnection(currDB) };
  };

  /**
   * URLs dos zips guardados.
   *
   * É a pergunta "tem zip para descompactar?" sem carregar blob nenhum: a chave da store é a
   * própria URL, então `getAllKeys` responde sem tocar no conteúdo. Ler os registros inteiros
   * traria dezenas de MB para a heap só para contar quantos são.
   */
  const getImagesZipKeysFromDB = async (): Promise<string[]> => {
    let currDB: IDBPDatabase | undefined;
    try {
      currDB = await openImagesDBConnection();
      const transaction = currDB.transaction(IMAGES_ZIP_STORE, 'readonly');
      const keys = await transaction.objectStore(IMAGES_ZIP_STORE).getAllKeys();
      await transaction.done;
      return keys.map((key) => String(key));
    } catch (error) {
      console.info('Erro ao listar os zips de imagens do banco:', error);
      return [];
    } finally {
      closeDBConnection(currDB);
    }
  };

  /** Um zip por vez — o blob é o custo de memória do processo, e só um cabe de cada vez. */
  const getImagesZipFromDB = async (url: string): Promise<ImagesZipRow | null> => {
    let currDB: IDBPDatabase | undefined;
    try {
      currDB = await openImagesDBConnection();
      const transaction = currDB.transaction(IMAGES_ZIP_STORE, 'readonly');
      const row = await transaction.objectStore(IMAGES_ZIP_STORE).get(url);
      await transaction.done;
      return (row as ImagesZipRow) ?? null;
    } catch (error) {
      console.error('Erro ao obter o zip de imagens do banco:', error);
      return null;
    } finally {
      closeDBConnection(currDB);
    }
  };

  /** Apaga o zip já descompactado: as imagens dele estão na store `imagens` e ele só ocupa disco. */
  const deleteImagesZipFromDB = async (url: string): Promise<void> => {
    let currDB: IDBPDatabase | undefined;
    try {
      currDB = await openImagesDBConnection();
      const transaction = currDB.transaction(IMAGES_ZIP_STORE, 'readwrite');
      void transaction
        .objectStore(IMAGES_ZIP_STORE)
        .delete(url)
        .catch(() => {});
      await transaction.done;
    } catch (error) {
      console.error('Erro ao apagar o zip de imagens do banco:', error);
    } finally {
      closeDBConnection(currDB);
    }
  };

  /**
   * Todas as imagens de uma vez.
   *
   * **Carrega o conjunto inteiro na memória** — os bytes vêm dentro de cada registro. O boot não
   * pré-carrega `imagens` (ver `LoaderContext`) e a tela usa `getImageFromDB`, uma por vez; se
   * alguém voltar a chamar esta função num aparelho fraco, é aqui que a memória estoura.
   */
  const getImagesFromDB = async (): Promise<TableTypes['imagens']> => {
    let currDB: IDBPDatabase | undefined;
    try {
      currDB = await openImagesDBConnection();
      const transaction = currDB.transaction(toCoded('imagens'), 'readonly');
      const store = transaction.objectStore(toCoded('imagens'));
      const rows = await store.getAll();
      await transaction.done;
      const out: Record<number, Blob | string> = {};
      if (Array.isArray(rows)) {
        for (const row of rows) {
          const cod = row?.COD_PRODUTO;
          if (cod == null || !Number.isFinite(Number(cod))) continue;
          const imagem = materializarImagem(row);
          if (imagem == null) continue;
          out[Number(cod)] = imagem;
        }
      }
      return Object.keys(out).length ? out : null;
    } catch (error) {
      console.error('Erro ao obter imagens do banco:', error);
      return null;
    } finally {
      closeDBConnection(currDB);
    }
  };

  const getOnDB = useCallback(
    async <T extends Tables>(
      name: T,
      client?: number,
      _retryCount: number = 0,
      force: boolean = false,
      enableFillTables: boolean = true
    ): Promise<TableTypes[T]> => {
      const safeName = normalizeTableName(String(name));
      if (!safeName) {
        console.warn(`Tabela desconhecida solicitada: ${String(name)}`);
        return [] as TableTypes[T];
      }

      const readCachedData = () => {
        const cachedData = tables.current?.[safeName];
        const hasCachedData = Array.isArray(cachedData)
          ? true
          : cachedData != null && (typeof cachedData !== 'object' || Object.keys(cachedData).length > 0);

        return hasCachedData ? (cachedData as TableTypes[T]) : null;
      };

      if (!force) {
        const cached = readCachedData();
        if (cached != null) return cached;

        const inFlight = inFlightTableLoads[safeName];
        if (inFlight) {
          return inFlight as Promise<TableTypes[T]>;
        }
      }

      const loadPromise = (async (): Promise<TableTypes[T]> => {
        isLoading.current[safeName] = true;

        try {
          if (!force) {
            const cached = readCachedData();
            if (cached != null) return cached;
          }

          if (safeName === 'imagens') {
            const images = (await getImagesFromDB()) as TableTypes['imagens'];
            setOnTables({}, 'imagens', force);
            return images;
          }

          let list: any;

          if (isLocalTable(safeName)) {
            const currDB = await openDBConnection();
            try {
              list = await handleGet(safeName, currDB);
            } finally {
              currDB.close();
            }
          } else {
            const cachedDicionario =
              safeName === 'produtos' &&
              tables.current?.dicionario_produtos &&
              Object.keys(tables.current.dicionario_produtos).length
                ? (tables.current.dicionario_produtos as Record<number, ProductProps>)
                : null;
            list = await hydrateFromBfSeller(safeName, client, cachedDicionario);
          }

          if (enableFillTables && name === 'catalogo' /* || name === 'categoria' */) setOnTables([{}], safeName, force);
          else if (enableFillTables) setOnTables(list, safeName, force);

          return list as TableTypes[T];
        } catch (error) {
          console.error(`Erro ao obter dados de ${safeName}:`, error);
          return [] as TableTypes[T];
        } finally {
          isLoading.current[safeName] = false;
        }
      })();

      if (!force) {
        inFlightTableLoads[safeName] = loadPromise;
      }

      loadPromise.finally(() => {
        if (inFlightTableLoads[safeName] === loadPromise) {
          delete inFlightTableLoads[safeName];
        }
      });

      return loadPromise;
    },
    [setOnTables]
  );

  const setOnDB = async (data: any, name: Tables, force: boolean = false): Promise<void> => {
    if (!name) {
      return Promise.reject(new Error('Chave ausente'));
    }

    if (!isValidDbPayload(data) && !force) {
      return Promise.reject(new Error('Dados inválidos'));
    }

    if (name === 'produtos') {
      return Promise.reject(new Error('Não é possível salvar dados em produtos'));
    }

    try {
      if (name === 'imagens') {
        await setImagesOnDB(data);
        return;
      }

      /**
       * Tabela de dataset: o dado mora no `BF_SELLER_DATASETS`, então a alteração local vai para lá.
       * a baixa de estoque do pedido valeria só até o próximo boot.
       */
      if (!isLocalTable(name)) {
        if (!BF_SELLER_STORES.includes(name as BfSellerStore)) {
          return Promise.reject(new Error(`Tabela derivada, não é possível salvar dados em ${name}`));
        }
        await writeLocalDatasetTable(name as BfSellerStore, data);
        return;
      }

      const currDB = await openDBConnection().catch((err) => {
        throw new Error(`Erro ao abrir conexão com banco de dados para salvar dados em ${name}: ${err?.message ?? err}`);
      });

      const transaction = currDB.transaction(toCoded(name), 'readwrite');
      const store = transaction.objectStore(toCoded(name));

      const allKeys = await store.getAllKeys().catch((err) => {
        throw new Error(`Erro ao obter chaves do banco de dados para salvar dados em ${name}: ${err?.message ?? err}`);
      });

      if (allKeys.length) {
        await store.delete(allKeys[allKeys.length - 1]).catch((err) => {
          throw new Error(`Erro ao deletar dados do banco de dados para salvar dados em ${name}: ${err?.message ?? err}`);
        });
      }

      await store.put({ data, keyPath: toCoded(name) }).catch((err) => {
        throw new Error(`Erro ao salvar dados no banco de dados para salvar dados em ${name}: ${err?.message ?? err}`);
      });

      await transaction.done.catch((err) => {
        throw new Error(`Erro ao fechar transação do banco de dados para salvar dados em ${name}: ${err?.message ?? err}`);
      });

      closeDBConnection(currDB);
    } catch (error) {
      console.error(`Erro ao salvar dados em ${name}:`, error);
      return Promise.reject(error);
    }
  };

  const reloadTables = async () => {
    for (const table of listTables) {
      if (table === 'catalogo' || table === 'download_registers') continue;
      await getOnDB(table, undefined, undefined, true);
    }
  };

  const validateTable = async (table: Tables): Promise<boolean> => {
    try {
      if (table === 'imagens') {
        const db = await openImagesDBConnection();
        // Zip gravado conta como imagem baixada: é ele que o sync passa a guardar. Sem isso, o
        // `temDado('imagens')` do `_useGetLinks` seria sempre falso e o zip voltaria a ser
        // baixado a cada sync.
        const transaction = db.transaction([toCoded(table), IMAGES_ZIP_STORE], 'readonly');
        const [imagens, zips] = await Promise.all([
          transaction.objectStore(toCoded(table)).count(),
          transaction.objectStore(IMAGES_ZIP_STORE).count(),
        ]);
        return !!(imagens || zips);
      }

      // Tabela de dataset: quem responde é o `__meta` do BF_SELLER_DATASETS.
      if (!isLocalTable(table)) return hasDatasetRows(table);

      const db = await openDBConnection();
      const transaction = db.transaction(toCoded(table), 'readonly');
      const total = await transaction.objectStore(toCoded(table)).count();
      return !!total;
    } catch (error) {
      console.info(`Erro ao validar existência de dados na tabela ${table}:`, error);
      return false;
    }
  };

  return {
    getOnDB,
    setOnDB,
    deleteDB,
    reloadTables,
    setImagesOnDB,
    setImagesZipOnDB,
    validateTable,
    getImageFromDB,
    deleteImagesDB,
    getImagesZipFromDB,
    getImagesZipKeysFromDB,
    deleteImagesZipFromDB,
    abrirSessaoImagens,
    closeDBConnection,
  };
};
